//This page will implement the facial liveness UI from AWS Amplify
// Route Location : /realTest/:webTicketId

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { ThemeProvider, Loader } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import awsexports from '../aws-exports';
import axios from 'axios';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Amplify.configure(awsexports);

// onLivenessResult is a function that will be passed from the parent component.
// It takes the result of the liveness test and the session id as arguments.
function SetProfileImage () {
  
  const { webTicketId, language } = useParams(); // Extract :webTicket and language from the URL

  // Normalize language from URL: 'eng'|'spa' (any case) → 'en'|'es'
  const normalizeLang = (l) => {
    if (!l) return 'en';
    const v = String(l).toLowerCase();
    if (v === 'spa' || v === 'es') return 'es';
    return 'en';
  };
  const langCode = normalizeLang(language);
  const isSpanish = langCode === 'es';

  const [isMatch, setIsMatch] = useState(null);
  const [loading, setLoading] = useState(true); // loading means we are creating the liveness session.
  const [sessionId, setSessionId] = useState(null);  //holds the liveness session id. 
  const [message, setMessage] = useState('');
  const [failureReason, setFailureReason] = useState(null); // 'liveness' | 'match' | null
  const [sessionError, setSessionError] = useState('');
 
  //First get a liveness session id from the backend when the component mounts.
  //Execute only once.
  const createSession = async () => {
    try {
      setLoading(true);
      setSessionError('');
      const resp = await fetch(`${process.env.REACT_APP_LIVENESS_VERIFY_CREATESESSION}/${webTicketId}`);
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg = data?.message || (isSpanish ? `Error al crear la sesión (${resp.status})` : `Create session failed (${resp.status})`);
        throw new Error(msg);
      }
      setSessionId(data.livenessSessionId);
    } catch (err) {
      console.error('Error creating liveness session:', err);
      setSessionError(err.message);
      toast.error((isSpanish ? 'Error al crear la sesión de liveness: ' : 'Error creating liveness session: ') + err.message);
      setSessionId(null);
    } finally {
      setLoading(false);
    }
  };


  //Execure on component mount.
   useEffect(() => {
    createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  //Get the liveness result when the analysis is complete.
  const handleAnalysisComplete = async () => {
      try {
        const url = `${process.env.REACT_APP_LIVENESS_NEWPROFILEPIC}/${webTicketId}`;
        const response = await axios.post(
          url,
          { sessionId },
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        const data = response.data;
        setIsMatch(true);
        setMessage(data?.message || (isSpanish ? 'Foto de perfil actualizada con éxito' : 'Profile Picture updated successfully'));
        setFailureReason(null);

      } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;

        if (status === 403) {
          const isLivenessFail = data?.isLive === false || data?.code === 'LIVENESS_NOT_PASSED';
          setIsMatch(false);
          setMessage(
            data?.message ||
            (isSpanish
              ? (isLivenessFail ? 'La verificación de liveness no pasó.' : 'La comparación facial falló.')
              : (isLivenessFail ? 'Liveness check did not pass.' : 'Face comparison failed.'))
          );
          setFailureReason(isLivenessFail ? 'liveness' : 'match');
          return;
        }

        const raw = data ? JSON.stringify(data) : (error?.message || '');
        console.error('Error verifying:', error);
        toast.error((isSpanish ? 'Error de verificación: ' : 'Verification error: ') + raw);
        setIsMatch(false);
        setMessage(isSpanish ? 'Ocurrió un error inesperado. Por favor, intenta de nuevo.' : 'An unexpected error occurred. Please try again.');
        setFailureReason('unknown');
      }
  };

  

  
  const handleRetry = async () => {
    setIsMatch(null);
    setMessage('');
    setFailureReason(null);
    await createSession(); // fetch a new session and remount detector
  };

  return (
    <ThemeProvider>
      {loading ? (
        <Loader />
      ) : !sessionId ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <p style={{ color: 'red' }}>{sessionError || (isSpanish ? 'No se puede crear la sesión de liveness.' : 'Unable to create liveness session.')}</p>
          <button onClick={createSession}>{isSpanish ? 'Intentar de nuevo' : 'Retry'}</button>
        </div>
      ) : (
        <>
          {isMatch === null ? (
            <FaceLivenessDetector
              key={sessionId}
              sessionId={sessionId}
              region="us-east-1"
              onAnalysisComplete={handleAnalysisComplete}
              onError={(error) => {
                console.error(error.stack);
                toast.error((isSpanish ? 'Error de detección de liveness: ' : 'Liveness detection error: ') + error.message);
              }}
              disableStartScreen={true}
              displayText={
                (function(){
                  const dict = {
                    en: null,
                    es: {
                      goodFitCaptionText: 'Buen ajuste',
                      goodFitAltText: 'Ilustración de un rostro encajando dentro de un óvalo.',
                      tooFarCaptionText: 'Demasiado lejos',
                      tooFarAltText:
                        'Ilustración de un rostro dentro de un óvalo; hay un espacio entre el perímetro de la cara y los límites del óvalo.',
                      startScreenBeginCheckText: 'Comenzar verificación de video',
                      hintCenterFaceText: 'Centra tu cara',
                      hintCenterFaceInstructionText:
                        'Antes de comenzar, centra tu cara a la cámara en la parte superior. Acércate hasta llenar el óvalo y mantente quieto hasta completar la verificación.',
                      hintFaceOffCenterText: 'La cara no está en el óvalo, céntrala.',
                      hintMoveFaceFrontOfCameraText: 'Mueve la cara frente a la cámara',
                      hintTooManyFacesText: 'Asegúrate de que solo haya una cara',
                      hintFaceDetectedText: 'Cara detectada',
                      hintCanNotIdentifyText: 'Mueve la cara frente a la cámara',
                      hintTooCloseText: 'Aléjate',
                      hintTooFarText: 'Acércate',
                      hintConnectingText: 'Conectando...',
                      hintVerifyingText: 'Verificando...',
                      hintCheckCompleteText: 'Verificación completa',
                      hintIlluminationTooBrightText: 'Muévete a un lugar con menos luz',
                      hintIlluminationTooDarkText: 'Muévete a un lugar más iluminado',
                      hintIlluminationNormalText: 'Iluminación normal',
                      hintHoldFaceForFreshnessText: 'Mantente quieto',
                      hintMatchIndicatorText: '50% completado. Sigue acercándote.',
                      cameraMinSpecificationsHeadingText:
                        'La cámara no cumple con las especificaciones mínimas',
                      cameraMinSpecificationsMessageText:
                        'La cámara debe soportar al menos 320×240 de resolución y 15 fps.',
                      cameraNotFoundHeadingText: 'No se puede acceder a la cámara',
                      cameraNotFoundMessageText:
                        'Verifica que haya una cámara conectada y que ninguna otra aplicación la esté usando. Revisa permisos del navegador, cierra otras apps y vuelve a intentar.',
                      waitingCameraPermissionText:
                        'Esperando a que permitas el uso de la cámara.',
                      a11yVideoLabelText: 'Cámara para verificación de liveness',
                      retryCameraPermissionsText: 'Reintentar',
                      cancelLivenessCheckText: 'Cancelar verificación',
                      photosensitivityWarningHeadingText: 'Advertencia de fotosensibilidad',
                      photosensitivityWarningBodyText:
                        'Esta verificación muestra luces de colores. Ten cuidado si eres fotosensible.',
                      photosensitivityWarningInfoText:
                        'Algunas personas pueden experimentar convulsiones epilépticas al exponerse a luces de colores. Ten precaución si tú o alguien de tu familia tiene esta condición.',
                      photosensitivityWarningLabelText:
                        'Más información sobre fotosensibilidad',
                      errorLabelText: 'Error',
                      connectionTimeoutHeaderText: 'Tiempo de conexión agotado',
                      connectionTimeoutMessageText: 'La conexión se agotó.',
                      timeoutHeaderText: 'Tiempo agotado',
                      timeoutMessageText:
                        'La cara no encajó en el óvalo a tiempo. Intenta de nuevo y llena por completo el óvalo.',
                      faceDistanceHeaderText: 'Movimiento hacia adelante detectado',
                      faceDistanceMessageText: 'Evita acercarte durante la conexión.',
                      multipleFacesHeaderText: 'Se detectaron múltiples caras',
                      multipleFacesMessageText:
                        'Asegúrate de que solo una cara esté frente a la cámara.',
                      clientHeaderText: 'Error del cliente',
                      clientMessageText: 'La verificación falló por un problema del cliente.',
                      serverHeaderText: 'Problema del servidor',
                      serverMessageText:
                        'No se puede completar la verificación por un problema del servidor.',
                      landscapeHeaderText: 'Orientación horizontal no compatible',
                      landscapeMessageText:
                        'Gira tu dispositivo a orientación vertical (retrato).',
                      portraitMessageText:
                        'Mantén el dispositivo en orientación vertical durante la verificación.',
                      tryAgainText: 'Intentar de nuevo',
                      recordingIndicatorText: 'Rec',
                    },
                  };
                  return dict[langCode];
                })()
              }
            />
          ) : isMatch ? (
            <>
              <p>{isSpanish ? 'Foto Actualizada' : 'Photo Updated'}</p>
              {message && <p>{message}</p>}
            </>
          ) : (
            <div style={{ color: 'red', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', alignItems: 'center', textAlign: 'center', minHeight: '40vh', fontWeight: 600 }}>
              <p>{message || (isSpanish ? 'Foto no actualizada' : 'Photo not updated')}</p>
              <button onClick={handleRetry}>{isSpanish ? 'Intentar de nuevo' : 'Try again'}</button>
              {failureReason === 'liveness' && <small>{isSpanish ? 'Consejo: Asegúrate de una buena iluminación y mantén tu cara centrada.' : 'Tip: Ensure good lighting and keep your face centered.'}</small>}
              {failureReason === 'match' && <small>{isSpanish ? 'Consejo: Asegúrate de que la foto del perfil del trabajador coincida con el usuario actual.' : 'Tip: Make sure the worker profile photo matches the current user.'}</small>}
            </div>
          )}
        </>
      )}
    </ThemeProvider>
  );
}

export default SetProfileImage;